import type { TenantConfig, TenantSite } from '../types/tenant-config'
import { getCountryMeta } from '../constants/country-metadata'
import { buildSiteUrl, buildLocaleUrl } from './tenant-url'

/**
 * Localized text templates for the GeoIP bar, keyed by language code.
 * Uses {brandName} and {country} as interpolation placeholders.
 */
const GEOIP_TEXT_TEMPLATES: Record<string, { geoIpBarText: string; continueText: string }> = {
  en: {
    geoIpBarText: 'You are viewing the {brandName} website for {country}.\nFor a better experience, we suggest browsing the site for your region.',
    continueText: 'Continue',
  },
  es: {
    geoIpBarText: 'Está viendo el sitio web de {brandName} para {country}.\nPara una mejor experiencia sugerimos que explore el sitio de su región.',
    continueText: 'Continuar',
  },
  fr: {
    geoIpBarText:
      'Vous consultez le site web de {brandName} pour le pays suivant {country}.\nPour optimiser votre expérience, nous vous conseillons de parcourir le site de votre région.',
    continueText: 'Continuer',
  },
  de: {
    geoIpBarText:
      'Sie befinden sich gerade auf der {brandName}-Website für folgendes Land {country}.\nSehen Sie sich bitte die Website für Ihre Region an, um ein gezielteres Einkaufserlebnis sicherzustellen.',
    continueText: 'Weiter',
  },
  nl: {
    geoIpBarText: 'Je bekijkt de {brandName}-website voor {country}.\nVoor een betere beleving raden we je aan om de site voor je regio te bekijken.',
    continueText: 'Doorgaan',
  },
  it: {
    geoIpBarText:
      "Stai visualizzando il sito web di {brandName} per {country}.\nPer un'esperienza ottimale, ti suggeriamo di visitare il sito relativo alla tua area geografica.",
    continueText: 'Continua',
  },
  pt: {
    geoIpBarText: 'Está a ver o web site da {brandName} para {country}.\nPara uma experiência melhor, sugerimos que aceda ao site da sua região.',
    continueText: 'Continuar',
  },
  da: {
    geoIpBarText: 'Du ser {brandName}s hjemmeside for {country}.\nVi foreslår, at du browser hjemmesiden for din region, for at få en bedre oplevelse.',
    continueText: 'Fortsæt',
  },
  sv: {
    geoIpBarText: 'Du tittar på {brandName}s webbplats för {country}.\nFör en bättre upplevelse föreslår vi att du besöker webbplatsen för din region.',
    continueText: 'Fortsätt',
  },
  nb: {
    geoIpBarText: 'Du er inne på {brandName} sine nettsider i {country}.\nVi anbefaler at du bruker de norske nettsidene for en best mulig opplevelse.',
    continueText: 'Fortsett',
  },
  fi: {
    geoIpBarText:
      'Katsot tällä hetkellä seuraavaa {brandName}-sivustoa {country}.\nParemman kokemuksen saamiseksi suosittelemme, että selaat sivustoa oman alueesi mukaan.',
    continueText: 'Jatka',
  },
}

function getGeoIpText(languageCode: string, brandName: string, countryName: string): { geoIpBarText: string; continueText: string } {
  const template = GEOIP_TEXT_TEMPLATES[languageCode] ?? GEOIP_TEXT_TEMPLATES['en']!
  return {
    geoIpBarText: template.geoIpBarText.replace(/{brandName}/g, brandName).replace(/{country}/g, countryName),
    continueText: template.continueText,
  }
}

/**
 * Represents a country entry for the GeoIP bar and country selector.
 * This shape is compatible with what the existing components expect.
 */
export interface DerivedCountryEntry {
  text: string
  url: string
  imageUrl: { image: { url: string } }
  locale: string
  analyticsId: string
  cloudflareCountryCode: string
  closeButtonText: string
  selectCountryText: string
  geoIpBarText?: string
  continueText?: string
  alternateLanguages: DerivedAlternateLanguage[]
}

export interface DerivedAlternateLanguage {
  url: string
  text: string
  analyticsId: string
  locale: string
  geoIpBarText?: string
  continueText?: string
  closeButtonText?: string
  selectCountryText?: string
}

/**
 * Derives the list of supported countries from the TenantConfig for the GeoIP bar
 * and country selector. Each country entry includes the URL, flag, localized text,
 * and alternate language options.
 *
 * @param tenantConfig - The resolved tenant configuration
 * @param currentSiteCountryName - The display name of the country whose site the user is
 *   currently viewing (e.g. "United States" when on vistaprint.com). Used to interpolate
 *   the {country} placeholder in GeoIP bar text. When omitted (e.g. from CountrySelector),
 *   the placeholder is left unreplaced.
 * @returns Array of country entries compatible with existing component interfaces
 */
export function deriveSupportedCountries(tenantConfig: TenantConfig, currentSiteCountryName?: string): DerivedCountryEntry[] {
  // Group sites by country code
  const countryGroups = new Map<string, TenantSite[]>()
  for (const site of tenantConfig.sites) {
    const existing = countryGroups.get(site.countryCode) || []
    existing.push(site)
    countryGroups.set(site.countryCode, existing)
  }

  const countries: DerivedCountryEntry[] = []

  for (const [countryCode, sites] of countryGroups) {
    const meta = getCountryMeta(countryCode)
    if (!meta) continue // Skip countries without metadata

    // Use the first site as the primary entry point for this country
    const primarySite = sites.find((s) => s.isPrimary) ?? sites[0]
    if (!primarySite) continue
    const primaryLocale = primarySite.locales.primary
    const languageCode = primaryLocale.split('-')[0]?.toLowerCase() ?? 'en'
    const geoIpText = getGeoIpText(languageCode, tenantConfig.brandName, currentSiteCountryName ?? '')

    // Build alternate languages for multi-locale sites
    const alternateLanguages: DerivedAlternateLanguage[] = []
    for (const site of sites) {
      for (const supportedLocale of site.locales.supported) {
        const altLangCode = supportedLocale.split('-')[0]?.toLowerCase() ?? 'en'
        const altMeta = getCountryMeta(countryCode)
        const altGeoIpText = getGeoIpText(altLangCode, tenantConfig.brandName, currentSiteCountryName ?? '')

        alternateLanguages.push({
          url: buildLocaleUrl(site, supportedLocale),
          text: altLangCode.toUpperCase(),
          analyticsId: '',
          locale: supportedLocale.toLowerCase(),
          geoIpBarText: altGeoIpText.geoIpBarText,
          continueText: altGeoIpText.continueText,
          closeButtonText: altMeta?.closeButtonText,
          selectCountryText: altMeta?.selectCountryText,
        })
      }
    }

    countries.push({
      text: meta.displayName,
      url: buildSiteUrl(primarySite),
      imageUrl: { image: { url: meta.flagUrl } },
      locale: primaryLocale.toLowerCase(),
      analyticsId: '',
      cloudflareCountryCode: meta.cloudflareCountryCode,
      closeButtonText: meta.closeButtonText,
      selectCountryText: meta.selectCountryText,
      geoIpBarText: geoIpText.geoIpBarText,
      continueText: geoIpText.continueText,
      alternateLanguages,
    })
  }

  return countries
}

/**
 * Derives the current country entry from tenant config based on the active locale.
 *
 * @param tenantConfig - The resolved tenant configuration
 * @param currentLocale - The current page locale
 * @returns The country entry for the current locale, or undefined if not found
 */
export function deriveCurrentCountry(tenantConfig: TenantConfig, currentLocale: string): DerivedCountryEntry | undefined {
  const countries = deriveSupportedCountries(tenantConfig)
  const normalizedLocale = currentLocale.toLowerCase()

  return countries.find((country) => country.locale === normalizedLocale || country.alternateLanguages.some((lang) => lang.locale === normalizedLocale))
}
